import { History, RefreshCw, RotateCw, Undo2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { BrebKeyDialog } from "@/shared/components/payments/breb-key/BrebKeyDialog";
import useTranslations from "@/shared/lang/useTranslations";

import { ConfirmSellDialog } from "../components/sell/ConfirmSellDialog";
import { SellCard } from "../components/sell/SellCard";
import { SellQuoteTicket } from "../components/sell/SellQuoteTicket";
import { CryptoSwapOrganizationPicker } from "../components/shared/CryptoSwapOrganizationPicker";
import { SavedWalletsDialog } from "../components/swap/SavedWalletsDialog";
import { VolumeDiscountBanner } from "../components/swap/VolumeDiscountBanner";
import { useCryptoSellPairs } from "../hooks/useCryptoSellData";
import { useCryptoSellForm } from "../hooks/useCryptoSellForm";
import { useCryptoSellFormMessages } from "../hooks/useCryptoSellFormMessages";
import { useCryptoSwapLabels } from "../hooks/useCryptoSwapLabels";
import { useOtcSavedWallets } from "../hooks/useOtcSavedWallets";
import { useOtcVolumeDiscount } from "../hooks/useOtcVolumeDiscount";
import { CryptoSellService } from "../services/cryptoSell.service";
import { cryptoSwapErrorText } from "../utils/cryptoSwapErrors";
import { CRYPTO_SELL_HISTORY_PATH } from "../utils/cryptoSwapLinks";

/** `/app/crypto-swaps/sell` — sell USDT from the organization's own wallet for pesos on its COP wallet. */
export default function CryptoSellPage() {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { can, isInternalUser } = useAuth();
  const isStaff = !!isInternalUser;
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isBrebKeyOpen, setIsBrebKeyOpen] = useState(false);
  const [isSavedWalletsOpen, setIsSavedWalletsOpen] = useState(false);

  useAppShellBreadcrumb(
    useMemo(
      () => [{ label: t("page_breadcrumb_wallets"), to: "/app/wallets" }, { label: t("sell_page_title") }],
      [t],
    ),
  );

  const canWrite = can("payments:write");
  const pairs = useCryptoSellPairs(isStaff ? organizationId : null);
  const organizationReady = !isStaff || !!organizationId;
  const savedWallets = useOtcSavedWallets(
    isStaff ? organizationId : null,
    organizationReady && pairs.hasAccess,
  );
  // The deposit comes from the organization's own wallet and returns to it: a third party's never applies.
  const ownWallets = useMemo(
    () => savedWallets.wallets.filter((wallet) => wallet.ownership === "OWN"),
    [savedWallets.wallets],
  );
  const refreshSavedWallets = savedWallets.refresh;
  const onWalletSaved = useCallback(() => refreshSavedWallets(), [refreshSavedWallets]);

  const form = useCryptoSellForm({
    pairs: pairs.pairs,
    canWrite,
    isStaff,
    organizationId: isStaff ? organizationId : null,
    onWalletSaved,
  });
  const { blockMessage, notice, quoteErrorText } = useCryptoSellFormMessages(form);
  const volumeDiscount = useOtcVolumeDiscount({
    quote: form.quote,
    isPricing: form.isPricing,
    anchor: form.anchor,
    amount: form.amount,
    isStaff,
    organizationId,
    load: CryptoSellService.volumeDiscount,
  });

  const pickedWallet = form.refund.savedWalletId
    ? ownWallets.find((wallet) => wallet.id === form.refund.savedWalletId)
    : undefined;
  const walletNote = pickedWallet
    ? t("confirm_saved_wallet", { alias: pickedWallet.alias })
    : form.refund.save && form.refund.alias.trim()
      ? t("sell_confirm_will_save_wallet", { alias: form.refund.alias.trim() })
      : null;

  if (!pairs.hasAccess) return <NoAccessView />;

  const organizationSlot = isStaff ? (
    <CryptoSwapOrganizationPicker
      id="crypto-sell-organization"
      mode="required"
      value={organizationId}
      onValueChange={setOrganizationId}
      requiredHint={t("sell_org_picker_required_hint")}
      orgHint={t("sell_org_picker_org_hint")}
    />
  ) : undefined;

  return (
    <div className="flex flex-col gap-6 pb-16">
      <ColocaPageHeader
        title={t("sell_page_title")}
        description={t("sell_page_description")}
        actions={
          <ColocaButton variant="default" icon={History} render={<Link to={CRYPTO_SELL_HISTORY_PATH} />}>
            {t("page_history_link")}
          </ColocaButton>
        }
      />

      {pairs.isLoading && <CatalogSkeleton />}

      {!pairs.isLoading && pairs.hasError && (
        <ColocaEmpty
          icon={RefreshCw}
          title={t("sell_pairs_load_failed")}
          description={t("error_service_unavailable")}
        >
          <ColocaButton variant="default" icon={RotateCw} onClick={pairs.refresh}>
            {t("pairs_retry")}
          </ColocaButton>
        </ColocaEmpty>
      )}

      {!pairs.isLoading && !pairs.hasError && pairs.isEmpty && (
        <ColocaEmpty icon={Undo2} title={t("sell_pairs_empty")} />
      )}

      {!pairs.isLoading && !pairs.hasError && !pairs.isEmpty && form.pair && (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px] xl:grid-cols-[minmax(0,1fr)_400px]">
          <SellCard
            form={form}
            organizationSlot={organizationSlot}
            disabled={!canWrite}
            onCreateBrebKey={canWrite ? () => setIsBrebKeyOpen(true) : undefined}
            savedWallets={ownWallets}
            canSaveWallet={canWrite}
            onManageSavedWallets={() => setIsSavedWalletsOpen(true)}
            volumeDiscount={
              volumeDiscount.view ? (
                <VolumeDiscountBanner
                  view={volumeDiscount.view}
                  pair={form.pair}
                  onApply={(suggestion) =>
                    form.applySuggestedAmount(volumeDiscount.applySuggestion(suggestion))
                  }
                />
              ) : undefined
            }
          />
          <SellQuoteTicket
            className="lg:sticky lg:top-6"
            pair={form.pair}
            quote={form.quote}
            isPricing={form.isPricing}
            clockOffsetMs={form.clockOffsetMs}
            networkLabel={labels.network(form.network)}
            notice={notice}
            blockMessage={blockMessage}
            canReview={form.block === null}
            onReview={form.openReview}
            onRequote={form.priceNow}
          />
        </div>
      )}

      <ConfirmSellDialog
        open={form.reviewOpen}
        onClose={form.closeReview}
        pair={form.pair}
        quote={form.quote}
        isPricing={form.isPricing}
        network={form.network}
        refundAddress={form.refund.address.trim()}
        walletNote={walletNote}
        concept={form.refund.concept.trim()}
        clockOffsetMs={form.clockOffsetMs}
        block={form.block}
        blockMessage={blockMessage}
        quoteErrorMessage={quoteErrorText}
        acknowledged={form.acknowledged}
        onAcknowledgedChange={form.setAcknowledged}
        phase={form.submitPhase}
        errorKind={form.submitError?.kind ?? null}
        errorCode={form.submitError?.code ?? null}
        errorMessage={form.submitError ? cryptoSwapErrorText(form.submitError, t) : null}
        onConfirm={() => void form.submit()}
        onRequote={form.priceNow}
      />

      <TwoFactorActionModals
        controller={form.saveWalletTwoFactor}
        requiredDescription={t("saved_wallet_two_factor_required")}
        challengeTitle={t("saved_wallet_two_factor_title")}
        challengeDescription={t("sell_saved_wallet_two_factor_description")}
      />

      <SavedWalletsDialog
        open={isSavedWalletsOpen}
        onClose={() => setIsSavedWalletsOpen(false)}
        wallets={ownWallets}
        canDelete={canWrite}
        onDelete={savedWallets.remove}
      />

      <BrebKeyDialog
        open={isBrebKeyOpen}
        organizationId={isStaff ? (organizationId ?? undefined) : undefined}
        onClose={() => {
          setIsBrebKeyOpen(false);
          pairs.refresh();
        }}
      />
    </div>
  );
}

function CatalogSkeleton() {
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <ColocaSkeleton className="h-[640px] rounded-3xl" />
      <ColocaSkeleton className="h-[440px] rounded-3xl" />
    </div>
  );
}
